<template>
	<section
		class="scene-pad-creator"
		v-bind:class="{
			'state-loading': !isPadReady,
		}"
	>
		<ViewContainer v-if="!isPadReady">
			<section class="pad-container">
				<div class="loading-overlay loading-overlay-dark"></div>
			</section>
		</ViewContainer>

		<ChalkyPadSceneDiagram
			v-if="isPadReady"
			class="push-bottom"
			v-bind:colors="colors"
			v-bind:diagramId="routerDiagramId"
			v-bind:portrait="isPortrait"
			v-bind:showBranding="showBranding"
			v-bind:showBreakbox="false"
			v-bind:showDiamonds="showDiamonds"
			v-bind:showFootspot="showFootspot"
			v-bind:showGrid="showGrid"
			v-bind:showHeadspot="showHeadspot"
			v-bind:showHeadstring="showHeadstring"
			v-bind:showOverlay="showOverlay"
			v-bind:showRackOutline="showRackOutline"
			v-bind:tableManager="tableManager"
			v-bind:tableSize="tableSize"
			v-bind:zoom="zoom"
			v-on:navigate="Handle_OnNavigate"
		>
			<template v-slot:practice="{ enableHaptics, enableMusic, enableSound, showTrajectories }">
				<ChalkyTrainerSceneFreeplay
					v-bind:diagramModel="clonedDiagramModel"
					v-bind:enableHaptics="enableHaptics"
					v-bind:enableMusic="enableMusic"
					v-bind:enableSound="enableSound"
					v-bind:environment="tableAppearance"
					v-bind:showTrajectories="showTrajectories"
					v-bind:tableAppearance="tableAppearance"
					v-if="trainerReady"
					v-on:game:ready="Handle_OnWorldReady"
				/>
			</template>
		</ChalkyPadSceneDiagram>
	</section>
</template>

<script lang="ts">
	import * as Service from '@/App/Service';
	import ChalkySticks, { type IDispatcherEvent } from '@chalkysticks/sdk';
	import SceneBase from '@/Core/Scene.vue';
	import { Component } from 'vue-property-decorator';
	import { Store as PadStore } from '@chalkysticks/vue2-pad';
	import { beforeDestroy, bind, created, mounted } from '@/Utility/Decorators';
	import { hexToTrainerTheme } from '@/Utility/Color';

	/**
	 * Whether the trainer has been loaded for the create page
	 *
	 * @type boolean
	 */
	let trainerLoaded: boolean = false;

	/**
	 * Scene for creating and editing pool shot diagrams with ball placement,
	 * settings panel, and practice mode integration.
	 *
	 * Follows the v3 Create page pattern: ActionToolbar owns table operations
	 * directly via tableManager prop, while the Scene handles coordination
	 * concerns like tutorial state and autosave.
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package Scene/Pad
	 * @project ChalkySticks App
	 */
	@Component
	export default class ScenePadCreator extends SceneBase {
		/**
		 * Diagram model from the pad store
		 *
		 * @return ChalkySticks.Model.Diagram
		 */
		protected get diagramModel(): ChalkySticks.Model.Diagram {
			return PadStore.getters['pad/diagramModel'];
		}

		/**
		 * Diagram ID from the router
		 *
		 * @return string
		 */
		protected get routerDiagramId(): string {
			return this.$route.params.id ? (this.$route.params.id as string) : '';
		}

		/**
		 * Human-readable table size from the store
		 *
		 * @return string
		 */
		protected get tableSize(): string {
			return PadStore.getters['pad/tableSize'];
		}

		/**
		 * Determine the trainer table appearance theme based on the
		 * current pad table color hex value.
		 *
		 * @return string
		 */
		protected get tableAppearance(): string {
			const colors = PadStore.getters['pad/colors'];
			const tableHex = colors?.table;

			if (!tableHex) {
				return 'Chalky';
			}

			return hexToTrainerTheme(tableHex);
		}

		/**
		 * Table manager instance from the pad store
		 *
		 * @return any
		 */
		protected get tableManager(): any {
			return PadStore.getters['pad/tableManager'];
		}

		/**
		 * Whether the pad library has been loaded and initialized
		 *
		 * @type boolean
		 */
		public isPadReady: boolean = false;

		/**
		 * Clone of the diagram model for use in the trainer to prevent
		 * mutations to the original model used by the pad.
		 *
		 * @type ChalkySticks.Model.Diagram
		 */
		protected clonedDiagramModel!: ChalkySticks.Model.Diagram;

		/**
		 * Whether the device is in portrait orientation
		 *
		 * @type boolean
		 */
		protected isPortrait: boolean = window.innerWidth < 450;

		/**
		 * Whether the trainer plugin has been loaded and initialized
		 *
		 * @type boolean
		 */
		protected trainerReady: boolean = false;

		/**
		 * Initialize the pad library, create the table manager, and load
		 * any existing diagram data.
		 *
		 * @return Promise<void>
		 */
		@created
		public async initializePad(): Promise<void> {
			// Initialize trainer plugin if not already loaded
			if (!trainerLoaded) {
				try {
					await Service.TrainerLoader.initialize();
					trainerLoaded = true;
				} catch (error) {
					console.error('[PageCreate] Failed to initialize trainer:', error);
				}
			}

			this.isPadReady = true;
		}

		/**
		 * @return void
		 */
		@mounted
		public attachEvents(): void {
			// ChalkySticks.Utility.Device.disablePullToRefresh();
			window.addEventListener('resize', this.Handle_OnWindowResize);
			ChalkySticks.Core.Event.Bus.on('pad.save:complete', this.Handle_OnPadSaveComplete);
			ChalkySticks.Core.Event.Bus.on('pad.load', this.Handle_OnPadTableLoaded);
		}

		/**
		 * @return void
		 */
		@beforeDestroy
		public detachEvents(): void {
			// ChalkySticks.Utility.Device.enablePullToRefresh();
			window.removeEventListener('resize', this.Handle_OnWindowResize);
			ChalkySticks.Core.Event.Bus.off('pad.change', this.Handle_OnPadChange);
			ChalkySticks.Core.Event.Bus.off('pad.load', this.Handle_OnPadTableLoaded);
			ChalkySticks.Core.Event.Bus.off('pad.save:complete', this.Handle_OnPadSaveComplete);
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * @param string item
		 * @return Promise<void>
		 */
		protected async Handle_OnNavigate(item: string): Promise<void> {
			if (item === 'practice') {
				setTimeout(() => {
					const tableState = this.tableManager.exportTableState();
					const cloned = new ChalkySticks.Pad.Model.Diagram(tableState);

					this.clonedDiagramModel = cloned;
					this.trainerReady = true;
				}, 150);
			} else {
				this.trainerReady = false;
			}
		}

		/**
		 * @param Event e
		 * @return Promise<void>
		 */
		protected async Handle_OnPadChange(e: IDispatcherEvent<any>): Promise<void> {
			// If we have an ID, navigate away
			if (this.routerDiagramId) {
				ChalkySticks.Core.Event.Bus.off('pad.change', this.Handle_OnPadChange);

				this.$router.push({
					name: 'PageCreate',
				});
			}
		}

		/**
		 * @param Event e
		 * @return Promise<void>
		 */
		protected async Handle_OnPadTableLoaded(e: IDispatcherEvent<any>): Promise<void> {
			ChalkySticks.Core.Event.Bus.off('pad.change', this.Handle_OnPadChange);
			ChalkySticks.Core.Event.Bus.on('pad.change', this.Handle_OnPadChange);
		}

		/**
		 * @param Event e
		 * @return Promise<void>
		 */
		protected async Handle_OnPadSaveComplete(e: IDispatcherEvent<any>): Promise<void> {
			const diagramId = e.data?.diagramId;
			const hash = e.data?.hash;

			// Navigate to the saved diagram page after saving
			if (diagramId && hash) {
				this.$router.push({
					name: 'PageCreate',
					params: {
						id: diagramId,
					},
				});
			}
		}

		/**
		 * Handle when the game is ready
		 *
		 * @param any gameInstance
		 * @return Promise<void>
		 */
		protected async Handle_OnWorldReady(gameInstance: any): Promise<void> {
			console.log('ChalkySticks Trainer ready:', gameInstance);

			// const balls = (this.diagramModel.getLayout()?.balls || []).map((ball: any) => {
			// 	ball.x = 1 - ball.normalizedX;
			// 	ball.y = 1 - ball.normalizedY;
			// 	return ball;
			// });

			// ChalkySticks.Core.Event.Bus.dispatch('game.control:rackCustom', balls);

			// mk: Hack to fix the problems in vue2-trainer right now
			window.dispatchEvent(new Event('resize'));
		}

		/**
		 * @param Event e
		 * @return Promise<void>
		 */
		@bind
		protected async Handle_OnWindowResize(e: Event): Promise<void> {
			this.isPortrait = window.innerWidth < 450;
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.scene-pad-creator {
		height: 100%;
		width: 100%;

		.english-overlay {
			user-select: none;

			.backdrop {
				opacity: 0.33;
			}

			.ball-container {
				opacity: 0.5;
			}
		}
	}

	// State
	// ---------------------------------------------------------------------------

	.scene-pad-creator {
		.view-extras-pad-tutorial ~ .btn-toggle-tutorial {
			opacity: 1;
		}
	}

	// Media Queries
	// ---------------------------------------------------------------------------

	@include media-query('mobile portrait') {
		.scene-pad-creator {
			.chalky.chalky-pad.scene-diagram .view-panel-diagram {
				padding: 80px 0 0 0;
			}
		}
	}
</style>
